import { acertusPalette } from '@metrogistics/acertus-ui/theme/acertusTheme';
import {
  ThemeOptions,
  createTheme,
  responsiveFontSizes
} from '@mui/material/styles';

const statusError = '#C50000';
const statusErrorOld = '#910c01';
const statusWarning = '#F2C94C';
const statusOk = '#5AB652';
const statusInfo = '#1677CE';

const white = '#FFFFFF';
const paper = '#D6D8DC';
const cardboard = '#858B91';
const black = '#000000';
const background = '#E5E5E5';
const trimMain = '#333E48';

export const primaryMain = '#ED7300';
const secondaryMain = '#1677CE';
const tertiaryMain = '#4C00ED';

const textPrimary = '#333E48';
const textSecondary = '#4F4F4F';
const textDisabled = '#bababb';

export const orange40 = '#F8C799';

declare module '@mui/material/styles' {
  interface TypeBackground {
    cardboard: string;
  }

  interface Palette {
    trim: Palette['primary'];
    tertiary: Palette['primary'];
    primaryDisabled: Palette['primary'];
  }
  interface PaletteOptions {
    trim: PaletteOptions['primary'];
    tertiary: PaletteOptions['primary'];
    primaryDisabled: PaletteOptions['primary'];
  }
}

const themeOptions: ThemeOptions = {
  acertusPalette,
  palette: {
    common: {
      white,
      black
    },
    primary: { main: primaryMain },
    secondary: { main: secondaryMain },
    tertiary: { main: tertiaryMain },
    background: {
      default: background,
      paper,
      cardboard
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
      disabled: textDisabled
    },
    error: { main: statusError },
    warning: { main: statusWarning },
    info: { main: statusInfo },
    success: { main: statusOk },
    trim: { main: trimMain },
    primaryDisabled: { main: orange40 },
    slate: { main: '333E48' },
    white: {
      light: '#ffffff',
      main: '#ffffff',
      dark: '#ffffff',
      contrastText: '#000'
    }
  },
  typography: { fontFamily: '"Noto Sans", sans-serif' },
  components: {
    MuiCard: {
      defaultProps: {
        sx: {
          borderRadius: 3,
          backgroundColor: white,
          borderColor: primaryMain
        }
      }
    },
    MuiButton: {
      defaultProps: {
        sx: {
          borderRadius: '20px',
          textTransform: 'none',
          boxShadow: 'none',
          backgroundColor: 'primary',
          color: white,
          fontWeight: 'bold'
        }
      }
    },
    MuiInputLabel: {
      defaultProps: {
        sx: {
          fontWeight: '700',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '0 0 0 8px',
          margin: '0 0 2px 0',
          color: textPrimary
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: { label: { fontSize: '11px', fontWeight: '300' } },
      defaultProps: {
        sx: {
          fontWeight: '700',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '0 0 0 0',
          margin: '0 0 4px 0',
          color: textPrimary
        }
      }
    },
    MuiFormLabel: {
      defaultProps: {
        sx: {
          fontWeight: '700',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '0 0 0 0',
          margin: '0 0 4px 0',
          color: textPrimary
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '12px 16px !important',
          '&:before': { color: '#ED7300' },
          '&:after': { color: '#ED7300' },
          '&::placeholder': {
            opacity: 1,
            color: '#8E959C',
            letterSpacing: 0.15,
            fontWeight: 300
          },
          '&.MuiInputBase-input': {
            opacity: 1,
            color: '#333E48',
            letterSpacing: 0.15,
            fontWeight: 400
          }
        },
        nativeInput: {
          '&::placeholder': {
            opacity: 1,
            color: '#8E959C',
            letterSpacing: 0.15,
            fontWeight: 300
          }
        },
        icon: { fill: '#ED7300' }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          '&.Mui-selected': {
            borderColor: '#fff',
            backgroundColor: '#fff',
            fontWeight: '600'
          },
          '&.Mui-disabled': { fontWeight: '400' },
          '&.Mui-focusVisible': { backgroundColor: '#fff' },
          '&.Mui-focusVisible:hover': { backgroundColor: '#EFF0F1 !important' },
          '&.Mui-selected:hover': { backgroundColor: '#EFF0F1 !important' }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .Mui-disabled': {
            opacity: 1,
            '-webkit-text-fill-color': '#5D6771 !important'
          },
          '&.Mui-disabled fieldset': { borderColor: '#BEC2C6 !important' },
          '& fieldset': { borderColor: '#70787F !important' },
          '&.Mui-error fieldset': { borderColor: `${statusError} !important` },
          '&.Mui-focused fieldset': {
            borderColor: '#70787F !important',
            borderWidth: '1px !important'
          }
        },
        input: {
          backgroundColor: white,
          borderColor: '#70787F',
          '&::placeholder': {
            opacity: 1,
            color: '#8E959C',
            letterSpacing: 0.15,
            fontWeight: 300
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          paddingLeft: '8px',
          '&.Mui-error': { color: `${statusError} !important` }
        }
      }
    },
    MuiSvgIcon: { styleOverrides: { root: { color: '#ED7300' } } },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          '& .MuiCalendarOrClockPicker-root': {
            width: '256px',
            maxHeight: '290px'
          },
          '& .MuiCalendarPicker-root': { width: '256px', maxHeight: '290px' },
          '& .MuiPickersCalendarHeader-root': {
            paddingLeft: '16px',
            paddingRight: '4px',
            marginTop: '8px'
          },
          '& .MuiDayPicker-header': {
            borderTop: '1px solid #9EA4AA99',
            borderBottom: '1px solid #9EA4AA99'
          },
          '& .MuiDayPicker-weekDayLabel': {
            width: '32px',
            height: '32px'
          },
          '& .MuiPickersDay-root': {
            backgroundColor: white,
            width: '32px',
            height: '32px'
          },
          '& .Mui-selected': {
            backgroundColor: `${white} !important`,
            border: `1px solid ${primaryMain}`,

            '&:hover': { backgroundColor: `${white} !important` },
            '&:focus': { backgroundColor: `${white} !important` }
          },
          '& .MuiYearPicker-root': { justifyContent: 'center' }
        }
      }
    }
  }
};

const themeRequestQuoteOptions: ThemeOptions = {
  acertusPalette,
  palette: {
    error: { main: statusErrorOld },
    trim: { main: trimMain },
    tertiary: { main: tertiaryMain },
    primaryDisabled: { main: orange40 },
    slate: { main: '333E48' },
    white: {
      light: '#ffffff',
      main: '#ffffff',
      dark: '#ffffff',
      contrastText: '#000'
    }
  },
  typography: { fontFamily: '"NotoSans", sans-serif' },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#333E48 !important',
          letterSpacing: '0.5',
          paddingLeft: '0 !important',
          fontSize: '14px !important',
          fontWeight: '400 !important',
          marginBottom: '6px !important'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-error': { borderColor: `${statusErrorOld} !important` },
          '&.Mui-error fieldset': {
            borderColor: `${statusErrorOld} !important`
          }
        },
        input: {
          borderRadius: '4px',
          padding: '12px 16px',
          color: '#333E48'
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: `${statusErrorOld} !important`,
          '&.Mui-error': { color: `${statusErrorOld} !important` }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          '& .MuiCalendarOrClockPicker-root': {
            width: '256px',
            maxHeight: '290px'
          },
          '& .MuiCalendarPicker-root': { width: '256px', maxHeight: '290px' },
          '& .MuiPickersCalendarHeader-root': {
            paddingLeft: '16px',
            paddingRight: '4px',
            marginTop: '8px'
          },
          '& .MuiDayPicker-header': {
            borderTop: '1px solid #9EA4AA99',
            borderBottom: '1px solid #9EA4AA99'
          },
          '& .MuiDayPicker-weekDayLabel': {
            width: '32px',
            height: '32px'
          },
          '& .MuiPickersDay-root': {
            backgroundColor: white,
            width: '32px',
            height: '32px'
          },
          '& .Mui-selected': {
            backgroundColor: `${white} !important`,
            border: `1px solid ${primaryMain}`,

            '&:hover': { backgroundColor: `${white} !important` },
            '&:focus': { backgroundColor: `${white} !important` }
          },
          '& .MuiYearPicker-root': { justifyContent: 'center' }
        }
      }
    }
  }
};

export const themeRequestQuote = responsiveFontSizes(
  createTheme(themeOptions, themeRequestQuoteOptions)
);

export default responsiveFontSizes(createTheme(themeOptions));
